/**
 * Central URL-hierarki och relationer.
 *
 * Speglar sidstrukturen på elmarknad.se (sitemap_index.xml) så att varje sida har
 * en tydlig kanonisk URL, en förälder och syskon. Används för brödsmulor,
 * canonical-taggar, BreadcrumbList-schema, interna länkar och sitemap.
 */

import { COMPACT_PAGES } from "@/data/compact";

export const SITE_URL = "https://elmarknad.se";

export interface HierarchyNode {
  /** Kanonisk sökväg utan avslutande slash. */
  path: string;
  /** Visningsnamn i brödsmulor och relaterade länkar. */
  label: string;
  /** Förälderns sökväg. Endast "/" saknar förälder. */
  parent?: string;
}

/** Sektioner (nivå 1) och deras undersidor (nivå 2+). */
const nodes: HierarchyNode[] = [
  { path: "/", label: "Hem" },

  // ── Elavtal ────────────────────────────────────────────────────────────
  { path: "/elavtal", label: "Elavtal", parent: "/" },
  { path: "/elavtal/jamfor", label: "Jämför elavtal", parent: "/elavtal" },
  { path: "/elavtal/byt", label: "Byt elavtal", parent: "/elavtal" },
  { path: "/elavtal/teckna", label: "Teckna elavtal", parent: "/elavtal" },
  { path: "/elavtal/manadspris", label: "Månadsprisavtal", parent: "/elavtal" },
  { path: "/elavtal/fast", label: "Fast elavtal", parent: "/elavtal" },
  { path: "/elavtal/timpris", label: "Timpris", parent: "/elavtal" },
  { path: "/elavtal/kvartspris", label: "Kvartspris", parent: "/elavtal" },
  { path: "/elavtal/rorligt", label: "Rörligt pris", parent: "/elavtal" },
  { path: "/elavtal/mixpris", label: "Mixpris", parent: "/elavtal" },
  { path: "/elavtal/portfolj", label: "Portföljavtal", parent: "/elavtal" },
  { path: "/elavtal/anvisat", label: "Anvisat elavtal", parent: "/elavtal" },
  { path: "/elavtal/tillsvidare", label: "Tillsvidarepris", parent: "/elavtal" },
  { path: "/elavtal/vintersakrat", label: "Vintersäkrat elavtal", parent: "/elavtal" },
  { path: "/elavtal/basta", label: "Bästa elavtalet", parent: "/elavtal" },
  { path: "/elavtal/billiga", label: "Billiga elavtal", parent: "/elavtal" },
  { path: "/elavtal/binda", label: "Binda elpriset", parent: "/elavtal" },
  { path: "/elavtal/black-friday", label: "Black Friday", parent: "/elavtal" },
  { path: "/elavtal/forsta-gangen", label: "Elavtal första gången", parent: "/elavtal" },
  { path: "/elavtal/sag-upp", label: "Säg upp elavtal", parent: "/elavtal" },
  { path: "/elavtal/vid-flytt", label: "Elavtal vid flytt", parent: "/elavtal" },
  { path: "/elavtal/villa", label: "Elavtal villa", parent: "/elavtal" },
  { path: "/elavtal/lagenhet", label: "Elavtal lägenhet", parent: "/elavtal" },
  { path: "/elavtal/fritidshus", label: "Elavtal fritidshus", parent: "/elavtal" },
  { path: "/elavtal/brf", label: "Elavtal BRF", parent: "/elavtal" },
  { path: "/elavtal/foretag", label: "Elavtal företag", parent: "/elavtal" },
  { path: "/elavtal/student", label: "Elavtal student", parent: "/elavtal" },
  { path: "/elavtal/pensionar", label: "Elavtal pensionär", parent: "/elavtal" },
  { path: "/elavtal/avtalsvaljaren", label: "Avtalsväljaren", parent: "/elavtal" },

  // ── Elpriser ───────────────────────────────────────────────────────────
  { path: "/elpriser", label: "Elpriser", parent: "/" },
  { path: "/elpriser/jamfor", label: "Jämför elpriser", parent: "/elpriser" },
  { path: "/elpriser/idag", label: "Elpriser idag", parent: "/elpriser" },
  { path: "/elpriser/imorgon", label: "Elpriser imorgon", parent: "/elpriser" },
  { path: "/elpriser/prognos", label: "Elprisprognos", parent: "/elpriser" },
  { path: "/prognos-api", label: "Prognos-API för företag", parent: "/elpriser" },
  { path: "/elpriser/historik", label: "Historiska elpriser", parent: "/elpriser" },
  { path: "/elpriser/elomrade-1", label: "Elområde SE1", parent: "/elpriser" },
  { path: "/elpriser/elomrade-2", label: "Elområde SE2", parent: "/elpriser" },
  { path: "/elpriser/elomrade-3", label: "Elområde SE3", parent: "/elpriser" },
  { path: "/elpriser/elomrade-4", label: "Elområde SE4", parent: "/elpriser" },

  // ── Spotpris ───────────────────────────────────────────────────────────
  { path: "/spotpris", label: "Spotpris", parent: "/" },
  { path: "/spotpris/idag", label: "Spotpris idag", parent: "/spotpris" },
  { path: "/spotpris/imorgon", label: "Spotpris imorgon", parent: "/spotpris" },
  { path: "/spotpris/elomrade-se1", label: "Spotpris SE1", parent: "/spotpris" },
  { path: "/spotpris/elomrade-se2", label: "Spotpris SE2", parent: "/spotpris" },
  { path: "/spotpris/elomrade-se3", label: "Spotpris SE3", parent: "/spotpris" },
  { path: "/spotpris/elomrade-se4", label: "Spotpris SE4", parent: "/spotpris" },

  // ── Elbolag ────────────────────────────────────────────────────────────
  { path: "/elbolag", label: "Elbolag", parent: "/" },
  { path: "/elbolag/jamfor", label: "Jämför elbolag", parent: "/elbolag" },
  { path: "/elbolag/byt", label: "Byt elbolag", parent: "/elbolag" },
  { path: "/elbolag/basta", label: "Bästa elbolagen", parent: "/elbolag" },
  { path: "/elbolag/billigaste", label: "Billigaste elbolagen", parent: "/elbolag" },

  // Dynamiska sektioner – kopplade till respektive elbolagssida
  { path: "/elhandel", label: "Elhandelsbolag", parent: "/elbolag" },
  { path: "/elnat", label: "Elnätsbolag", parent: "/elbolag" },
  { path: "/elpriser/:slug", label: "Elpriser per kommun", parent: "/elpriser" },
  { path: "/solceller", label: "Solceller", parent: "/" },
  { path: "/solceller/solcellskalkylator", label: "Solcellskalkylator", parent: "/solceller" },
  { path: "/batterier", label: "Hembatterier", parent: "/" },
  { path: "/batterier/batterikalkylator", label: "Batterikalkylator", parent: "/batterier" },
  { path: "/laddboxar", label: "Laddboxar", parent: "/" },
  { path: "/laddboxar/laddboxkalkylator", label: "Laddboxkalkylator", parent: "/laddboxar" },
  { path: "/laddstolpar", label: "Laddstolpar", parent: "/" },
  { path: "/varmepumpar", label: "Värmepumpar", parent: "/" },
  { path: "/varmepumpar/varmepumpskalkylator", label: "Värmepumpskalkylator", parent: "/varmepumpar" },

  // ── Verktyg ────────────────────────────────────────────────────────────
  { path: "/verktyg", label: "Verktyg", parent: "/" },
  { path: "/verktyg/hitta-elavtal", label: "Hitta elavtal", parent: "/verktyg" },
  { path: "/verktyg/hitta-elbolag", label: "Hitta elbolag", parent: "/verktyg" },
  { path: "/verktyg/hitta-elomrade", label: "Hitta elområde", parent: "/verktyg" },
  { path: "/verktyg/hitta-natagare", label: "Hitta nätägare", parent: "/verktyg" },
  { path: "/verktyg/hitta-anlaggnings-id", label: "Hitta anläggnings-ID", parent: "/verktyg" },
  { path: "/verktyg/mobilapp", label: "Mobilapp", parent: "/verktyg" },
  { path: "/kalkylatorer", label: "Kalkylatorer", parent: "/" },

  // ── Redaktionellt ──────────────────────────────────────────────────────
  { path: "/blogg", label: "Blogg", parent: "/" },
  { path: "/nyheter", label: "Nyheter", parent: "/" },
  { path: "/rapporter", label: "Rapporter", parent: "/" },
  { path: "/ordlista", label: "Ordlista", parent: "/" },

  // ── Elmarknaden ────────────────────────────────────────────────────────
  { path: "/elmarknaden", label: "Elmarknaden", parent: "/" },
  { path: "/elmarknaden/sa-fungerar-elmarknaden", label: "Så fungerar elmarknaden", parent: "/elmarknaden" },
  { path: "/elmarknaden/historia", label: "Elmarknadens historia", parent: "/elmarknaden" },
  { path: "/elmarknaden/avregleringen", label: "Avregleringen av elmarknaden", parent: "/elmarknaden" },
  { path: "/elmarknaden/framtiden", label: "Framtidens elmarknad", parent: "/elmarknaden" },
  { path: "/produktionsstatus", label: "Elproduktion", parent: "/elmarknaden" },

  // ── Företag & kundtjänst ───────────────────────────────────────────────
  { path: "/om-oss", label: "Om oss", parent: "/" },
  { path: "/kontakt", label: "Kontakt", parent: "/" },
  { path: "/press", label: "Press", parent: "/" },
  { path: "/pressreleaser", label: "Pressreleaser", parent: "/press" },
  { path: "/jobba-hos-oss", label: "Jobba hos oss", parent: "/" },
  { path: "/bli-partner", label: "Bli partner", parent: "/" },
  { path: "/fragor-svar", label: "Frågor & svar", parent: "/" },
  { path: "/anvandarvillkor", label: "Användarvillkor", parent: "/" },
  { path: "/dataskyddspolicy", label: "Dataskyddspolicy", parent: "/" },
  { path: "/cookies", label: "Cookies", parent: "/" },
  { path: "/app-villkor", label: "Användarvillkor för appen", parent: "/" },
  { path: "/app-integritetspolicy", label: "Integritetspolicy för appen", parent: "/" },
  { path: "/anger-klagomal", label: "Ånger & klagomål", parent: "/kontakt" },
  { path: "/lamna-synpunkt", label: "Lämna synpunkt", parent: "/kontakt" },
  { path: "/felanmalan", label: "Felanmälan", parent: "/kontakt" },
  { path: "/skapa-arende", label: "Skapa ärende", parent: "/kontakt" },
  
  { path: "/solcellsofferter", label: "Solcellsofferter", parent: "/solceller" },
  { path: "/partneransokan", label: "Partneransökan", parent: "/bli-partner" },
  { path: "/spontanansokan", label: "Spontanansökan", parent: "/jobba-hos-oss" },

  // Jämförelsetjänster
  { path: "/elbyte", label: "Elbyte", parent: "/elbolag/jamfor" },
];

/** Compact keyword-sidor: branscher under "Elavtal för företag", övriga under sin vertikal. */
nodes.push(
  ...COMPACT_PAGES.map((p) => ({
    path: `/${p.vertical}/${p.slug}`,
    label: p.navLabel,
    parent: p.kind === "bransch" ? "/elavtal/foretag" : `/${p.vertical}`,
  })),
);

export const hierarchy: Record<string, HierarchyNode> = Object.fromEntries(
  nodes.map((n) => [n.path, n]),
);

/** Alias → kanonisk sökväg. Alla alias 301:as i routes.tsx och utesluts ur sitemap. */
export const canonicalAliases: Record<string, string> = {
  "/elpriser/se1": "/elpriser/elomrade-1",
  "/spotpris/se1": "/spotpris/elomrade-se1",
  "/spotpriser/se1": "/spotpris/elomrade-se1",
  "/elpriser/se2": "/elpriser/elomrade-2",
  "/spotpris/se2": "/spotpris/elomrade-se2",
  "/spotpriser/se2": "/spotpris/elomrade-se2",
  "/elpriser/se3": "/elpriser/elomrade-3",
  "/spotpris/se3": "/spotpris/elomrade-se3",
  "/spotpriser/se3": "/spotpris/elomrade-se3",
  "/elpriser/se4": "/elpriser/elomrade-4",
  "/spotpris/se4": "/spotpris/elomrade-se4",
  "/spotpriser/se4": "/spotpris/elomrade-se4",
  "/dagens-elpriser": "/elpriser/idag",
  "/morgondagens-elpriser": "/elpriser/imorgon",
  "/historiska-elpriser": "/elpriser/historik",
  "/ai-prognoser": "/elpriser/prognos",
  "/hitta-natagare": "/verktyg/hitta-natagare",
  "/hitta-elomrade": "/verktyg/hitta-elomrade",
  "/verktyg-tjanster": "/verktyg",
  
  "/vilket-elbolag": "/verktyg/hitta-elbolag",
  "/elbolag/elhandel": "/elhandel",
  "/elbolag/elnat": "/elnat",
  "/elhandelsbolag": "/elhandel",
  "/elnatsbolag": "/elnat",
  
  
  "/mobilapp": "/verktyg/mobilapp",
};

/** Sektioner vars detaljsidor renderas dynamiskt från scraped_pages. */
export const dynamicSections: Record<string, { label: string; listPath: string }> = {
  "/elhandel": { label: "Elhandelsbolag", listPath: "/elhandel" },
  "/elnat": { label: "Elnätsbolag", listPath: "/elnat" },
  "/elpriser": { label: "Elpriser per kommun", listPath: "/elpriser" },
  "/solceller": { label: "Solceller", listPath: "/solceller" },
  "/blogg": { label: "Blogg", listPath: "/blogg" },
  "/nyheter": { label: "Nyheter", listPath: "/nyheter" },
  "/rapporter": { label: "Rapporter", listPath: "/rapporter" },
  "/batterier": { label: "Hembatterier", listPath: "/batterier" },
  "/laddboxar": { label: "Laddboxar", listPath: "/laddboxar" },
  "/laddstolpar": { label: "Laddstolpar", listPath: "/laddstolpar" },
  "/varmepumpar": { label: "Värmepumpar", listPath: "/varmepumpar" },
};

/** Normaliserar en sökväg: tar bort avslutande slash och query. */
export function normalizePath(path: string): string {
  const clean = (path.split("?")[0] ?? "").split("#")[0] ?? "";
  const trimmed = clean.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/** Kanonisk sökväg för en given sökväg (löser alias). */
export function canonicalPath(path: string): string {
  const p = normalizePath(path);
  return canonicalAliases[p] ?? p;
}

/** Absolut canonical-URL. */
export function canonicalUrl(path: string): string {
  const p = canonicalPath(path);
  return p === "/" ? `${SITE_URL}/` : `${SITE_URL}${p}`;
}

/**
 * Förenklat sidnamn för <title>, og:title och brödsmulan.
 * Använder i första hand hierarkins korta etikett (t.ex. "Elprisprognos"),
 * annars kortas den långa rubriken av vid första skiljetecknet och
 * varumärkessuffix som "| Elmarknad" tas bort.
 */
/** Hierarkins korta etikett för en sökväg, om den finns (ej startsidan). */
export function hierarchyLabel(path: string): string | undefined {
  const label = hierarchy[canonicalPath(path)]?.label;
  return label && label !== "Hem" ? label : undefined;
}

export function shortPageTitle(path: string, fallbackTitle: string): string {
  const label = hierarchy[canonicalPath(path)]?.label;
  if (label && label !== "Hem") return label;

  const withoutBrand = fallbackTitle
    .replace(/\s*[|｜]\s*Elmarknad(\.se)?\s*$/i, "")
    .trim();
  const cut = withoutBrand.split(/\s+[–—-]\s+/)[0]?.trim() ?? withoutBrand;
  return cut.length >= 3 ? cut : withoutBrand;
}

export interface Crumb {
  label: string;
  path?: string | undefined;
  current?: boolean;
}

/**
 * Bygger brödsmulor för en sökväg genom att följa förälderkedjan.
 * Okända (t.ex. dynamiska) sidor får sin sektion som förälder.
 */
export function getBreadcrumbs(path: string, currentLabel?: string): Crumb[] {
  const target = canonicalPath(path);
  const chain: HierarchyNode[] = [];

  let node = hierarchy[target];
  if (!node) {
    // Dynamisk detaljsida: härled sektionen från första segmentet.
    const section = `/${target.split("/")[1] ?? ""}`;
    const sectionNode = hierarchy[section];
    node = {
      path: target,
      label: currentLabel ?? target.split("/").pop() ?? "Sida",
      parent: sectionNode ? section : "/",
    };
  }

  let cursor: HierarchyNode | undefined = node;
  const guard = new Set<string>();
  while (cursor && !guard.has(cursor.path)) {
    guard.add(cursor.path);
    chain.unshift(cursor);
    cursor = cursor.parent ? hierarchy[cursor.parent] : undefined;
  }

  if (chain[0]?.path !== "/") chain.unshift(hierarchy["/"]!);

  return chain.map((n, i) => {
    const isLast = i === chain.length - 1;
    return {
      label: isLast ? currentLabel ?? n.label : n.label,
      path: isLast ? undefined : n.path,
      current: isLast,
    };
  });
}

/** Föräldernoden för en sökväg. */
export function getParent(path: string): HierarchyNode | undefined {
  const node = hierarchy[canonicalPath(path)];
  return node?.parent ? hierarchy[node.parent] : undefined;
}

/** Direkta barn till en sökväg. */
export function getChildren(path: string): HierarchyNode[] {
  const p = canonicalPath(path);
  return nodes.filter((n) => n.parent === p);
}

/** Syskon (samma förälder), exklusive sidan själv. */
export function getSiblings(path: string): HierarchyNode[] {
  const p = canonicalPath(path);
  const node = hierarchy[p];
  const parent = node?.parent ?? `/${p.split("/")[1] ?? ""}`;
  return nodes.filter((n) => n.parent === parent && n.path !== p);
}

/** BreadcrumbList-schema (JSON-LD) för en sökväg. */
export function breadcrumbJsonLd(path: string, currentLabel?: string) {
  const crumbs = getBreadcrumbs(path, currentLabel);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      item: canonicalUrl(c.path ?? canonicalPath(path)),
    })),
  };
}

/** Alla kanoniska statiska sökvägar (för sitemap). */
export const canonicalPaths: string[] = nodes
  .map((n) => n.path)
  .filter((p) => !(p in canonicalAliases));
